import {
    useHasInvoiceCounterpartiesInWorkspace,
    useHasInvoicesInWorkspace,
} from "@api/invoice.api";
import {
    RenderableTableEmptyViewVariant,
    useTableEmptyView,
} from "@design-system/tableEmptyView/useTableEmptyView";
import { useCallback } from "react";
import {
    InvoiceTableEmptyView,
    InvoiceTableViewKind,
} from "./InvoiceTableEmptyView";

const FOOTER_HIDDEN_VARIANTS: readonly RenderableTableEmptyViewVariant[] = [
    "connect",
    "no-data",
];

interface UseInvoiceTableEmptyViewParams {
    isLoading: boolean;
    filteredCount: number;
    hasConnectedInvoiceAccounts: boolean;
    onClearFilters: () => void;
    viewKind: InvoiceTableViewKind;
}

export function useInvoiceTableEmptyView({
    isLoading,
    filteredCount,
    hasConnectedInvoiceAccounts,
    onClearFilters,
    viewKind,
}: UseInvoiceTableEmptyViewParams) {
    const isEmpty = !isLoading && filteredCount === 0;
    const isCounterpartyView = viewKind === "customers";

    const invoicesProbe = useHasInvoicesInWorkspace(
        isEmpty && !isCounterpartyView,
    );
    const counterpartiesProbe = useHasInvoiceCounterpartiesInWorkspace(
        isEmpty && isCounterpartyView,
    );
    const { hasRecords, isChecking } = isCounterpartyView
        ? counterpartiesProbe
        : invoicesProbe;

    const renderEmptyView = useCallback(
        (variant: RenderableTableEmptyViewVariant) => (
            <InvoiceTableEmptyView
                variant={variant}
                viewKind={viewKind}
                onClearFilters={onClearFilters}
            />
        ),
        [onClearFilters, viewKind],
    );

    const { emptyView, shouldHideFooter } = useTableEmptyView({
        isEmpty,
        hasConnectedAccounts: hasConnectedInvoiceAccounts,
        hasRecordsInWorkspace: hasRecords,
        isCheckingWorkspaceRecords: isChecking,
        renderEmptyView,
        footerHiddenVariants: FOOTER_HIDDEN_VARIANTS,
    });

    return { emptyView, shouldHideFooter };
}
